/**
 * 日期/时间格式化、文件大小格式化
 */

const BEIJING_TIME_ZONE = 'Asia/Shanghai';
const TIME_ZONE_PATTERN = /(Z|[+-]\d{2}:?\d{2})$/i;
declare const BEIJING_DATE_TIME_TEXT: unique symbol;

/**
 * 已转换为北京时间的最终展示文本。
 *
 * 用于 OpenDataFrame 等会直接渲染字符串的边界，防止误把 API UTC 原值赋给
 * sendTime/msgTime 后在页面显示 ISO 8601。
 */
export type BeijingDateTimeText = string & {
  readonly [BEIJING_DATE_TIME_TEXT]: true;
};

function parseDateInput(date: string | number | Date): Date {
  if (date instanceof Date) return date;
  if (typeof date === 'number') {
    return new Date(date < 1_000_000_000_000 ? date * 1000 : date);
  }

  const value = date.trim();
  if (!value) return new Date(NaN);
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    return new Date(`${value}T00:00:00+08:00`);
  }
  if (TIME_ZONE_PATTERN.test(value)) {
    return new Date(value);
  }
  // 后端 DateTime 以 UTC 存储；无时区字符串按 UTC 解析，再统一转北京时间。
  return new Date(`${value.replace(' ', 'T')}Z`);
}

/**
 * 保留 UTC 时间语义交给最终展示组件格式化，避免适配层先格式化为北京时间后被二次转换。
 */
export function normalizeUtcDateTimeValue(date: string | number | Date): string {
  if (date instanceof Date) return date.toISOString();
  if (typeof date === 'number') {
    return new Date(date < 1_000_000_000_000 ? date * 1000 : date).toISOString();
  }
  return date.trim();
}

function getBeijingParts(date: Date): Record<string, string> {
  return new Intl.DateTimeFormat('zh-CN', {
    timeZone: BEIJING_TIME_ZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(date).reduce<Record<string, string>>((acc, part) => {
    if (part.type !== 'literal') acc[part.type] = part.value;
    return acc;
  }, {});
}

// 日期格式化
export function formatDate(
  date: string | number | Date,
  fmt = 'yyyy-MM-dd'
): string {
  try {
    const d = parseDateInput(date);
    if (isNaN(d.getTime())) return '-';
    const parts = getBeijingParts(d);
    return fmt
      .replace(/yyyy/g, parts.year)
      .replace(/MM/g, parts.month)
      .replace(/dd/g, parts.day)
      .replace(/HH/g, parts.hour)
      .replace(/mm/g, parts.minute)
      .replace(/ss/g, parts.second);
  } catch {
    return '-';
  }
}

// 日期时间格式化
export function formatDateTime(
  date: string | number | Date,
  fmt = 'yyyy-MM-dd HH:mm:ss'
): BeijingDateTimeText {
  return formatDate(date, fmt) as BeijingDateTimeText;
}

// 最近消息时间：今天显示 HH:mm，本年显示 MM-dd，跨年显示 yyyy
export function formatRecentMessageTime(date: string | number | Date): string {
  try {
    const d = parseDateInput(date);
    if (isNaN(d.getTime())) return '';
    const parts = getBeijingParts(d);
    const nowParts = getBeijingParts(new Date());

    if (
      parts.year === nowParts.year &&
      parts.month === nowParts.month &&
      parts.day === nowParts.day
    ) {
      return `${parts.hour}:${parts.minute}`;
    }
    if (parts.year === nowParts.year) {
      return `${parts.month}-${parts.day}`;
    }
    return parts.year;
  } catch {
    return '';
  }
}

// 相对时间（如"3分钟前"）
export function timeAgo(date: string | number | Date): string {
  try {
    const d = parseDateInput(date);
    const now = Date.now();
    const diff = now - d.getTime();
    const seconds = Math.floor(diff / 1000);
    if (seconds < 60) return '刚刚';
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes}分钟前`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}小时前`;
    const days = Math.floor(hours / 24);
    if (days < 30) return `${days}天前`;
    return formatDate(d, 'yyyy-MM-dd');
  } catch {
    return '-';
  }
}

// 文件大小格式化
export function formatFileSize(bytes: number): string {
  if (bytes === 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB'];
  const k = 1024;
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + units[i];
}

// 数字千分位格式化
export function formatNumber(num: number): string {
  return num.toLocaleString('zh-CN');
}

// 截断字符串
export function truncate(str: string, maxLen = 50): string {
  if (!str || str.length <= maxLen) return str;
  return str.slice(0, maxLen) + '...';
}
